import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import DescriptionIcon from '@mui/icons-material/Description'
import LinkIcon from '@mui/icons-material/Link'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { makeGetRequest, makePostRequest, makeDeleteRequest } from 'utils/api'

const ContactDocuments = ({ contactId }) => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [linkDocumentOpen, setLinkDocumentOpen] = useState(false)
  const [availableDocuments, setAvailableDocuments] = useState([])
  const [selectedDocument, setSelectedDocument] = useState('')
  const [relationType, setRelationType] = useState('related')

  useEffect(() => {
    fetchDocuments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId])

  const fetchDocuments = async () => {
    try {
      setLoading(true)
      const response = await makeGetRequest(
        `/api/Social/contact/${contactId}/documents/`,
        {},
        true
      )
      if (response.status === 200) {
        setData(response.data)
      } else {
        setError('Failed to load documents')
      }
    } catch (err) {
      setError(err.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const fetchAvailableDocuments = async () => {
    try {
      const response = await makeGetRequest('/api/Social/available-documents/', {}, true)
      if (response.status === 200) {
        setAvailableDocuments(response.data.documents || [])
      }
    } catch (err) {
      console.error('Failed to fetch available documents:', err)
    }
  }

  const handleOpenLinkDialog = () => {
    fetchAvailableDocuments()
    setLinkDocumentOpen(true)
  }

  const handleLinkDocument = async () => {
    try {
      const response = await makePostRequest(
        `/api/Social/contact/${contactId}/link-document/`,
        {
          document_id: selectedDocument,
          relation_type: relationType,
        },
        {},
        true
      )
      if (response.status === 200) {
        alert('Document linked successfully!')
        setLinkDocumentOpen(false)
        setSelectedDocument('')
        setRelationType('related')
        fetchDocuments()
      }
    } catch (err) {
      alert('Failed to link document: ' + err.message)
    }
  }

  const handleUnlinkDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to unlink this document?')) {
      return
    }

    try {
      const response = await makeDeleteRequest(
        `/api/Social/contact/${contactId}/unlink-document/${documentId}/`,
        {},
        true
      )
      if (response.status === 200) {
        alert('Document unlinked successfully!')
        fetchDocuments()
      }
    } catch (err) {
      alert('Failed to unlink document: ' + err.message)
    }
  }

  const handleOpenDocument = (doc) => {
    if (doc.file_url) {
      window.open(doc.file_url, '_blank')
    } else if (doc.external_url) {
      window.open(doc.external_url, '_blank')
    } else {
      alert('No file or URL available for this document')
    }
  }

  const getSourceIcon = (source) => {
    switch (source) {
      case 'file':
        return <DescriptionIcon />
      case 'url':
        return <LinkIcon />
      default:
        return <DescriptionIcon />
    }
  }

  const getSourceColor = (source) => {
    switch (source) {
      case 'file':
        return 'primary'
      case 'url':
        return 'info'
      default:
        return 'default'
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    )
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        Aucun document disponible
      </Alert>
    )
  }

  const { summary, documents, documents_by_type, contact } = data

  return (
    <Box sx={{ p: 2 }}>
      {/* Summary Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ color: 'white' }}>
            Documents de {contact.full_name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'grey.400' }}>
            {summary.total_documents} Document(s) in {summary.types_count} categor
            {summary.types_count === 1 ? 'y' : 'ies'}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenLinkDialog}>
          Ajouter un document
        </Button>
      </Box>

      {/* Documents Table */}
      {documents.length === 0 ? (
        <Alert severity="info">Aucun document disponible</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Relation</TableCell>
                <TableCell>Date d'importation</TableCell>
                <TableCell>Date d'expiration</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getSourceIcon(doc.source)}
                      <Typography variant="body2" fontWeight="medium">
                        {doc.nom}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {doc.type ? (
                      <Chip label={doc.type} size="small" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {doc.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={doc.source}
                      size="small"
                      color={getSourceColor(doc.source)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={doc.relation_type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {doc.date_import ? new Date(doc.date_import).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    {doc.date_echeance ? (
                      <Chip
                        label={new Date(doc.date_echeance).toLocaleDateString()}
                        size="small"
                        color="warning"
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDocument(doc)}
                      disabled={doc.source === 'none'}
                      title="Open document"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleUnlinkDocument(doc.id)}
                      color="error"
                      title="Unlink document"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Documents par type */}
      {Object.keys(documents_by_type).length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
            Documents par type
          </Typography>
          {Object.entries(documents_by_type).map(([type, docs]) => (
            <Card key={type} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  {type} ({docs.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {docs.map((doc) => (
                    <Chip
                      key={doc.id}
                      label={doc.nom}
                      size="small"
                      onClick={() => handleOpenDocument(doc)}
                      disabled={doc.source === 'none'}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Link Document Dialog */}
      <Dialog
        open={linkDocumentOpen}
        onClose={() => setLinkDocumentOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Ajouter un document</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Document</InputLabel>
            <Select
              value={selectedDocument}
              onChange={(e) => setSelectedDocument(e.target.value)}
              label="Document"
            >
              {availableDocuments.map((doc) => (
                <MenuItem key={doc.id} value={doc.id}>
                  {doc.nom} {doc.type && `(${doc.type})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Type de relation</InputLabel>
            <Select
              value={relationType}
              onChange={(e) => setRelationType(e.target.value)}
              label="Type de relation"
            >
              <MenuItem value="related">Lié</MenuItem>
              <MenuItem value="owner">Propriétaire</MenuItem>
              <MenuItem value="signatory">Signataire</MenuItem>
              <MenuItem value="beneficiary">Bénéficiaire</MenuItem>
              <MenuItem value="witness">Témoin</MenuItem>
              <MenuItem value="other">Autre</MenuItem>
            </Select>
          </FormControl>

          {availableDocuments.length === 0 && (
            <Alert severity="info">
              Aucun document disponible. Créez des documents dans la section Administrative.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDocumentOpen(false)}>Annuler</Button>
          <Button onClick={handleLinkDocument} variant="contained" disabled={!selectedDocument}>
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ContactDocuments
